import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useReveal } from "@/hooks/useReveal";

type ContentItem =
  | { type: "text"; content: string }
  | { type: "bullets"; items: string[] };

type Section = {
  title: string;
  content: ContentItem[];
};

const sections: Section[] = [
  {
    title: "1. Introduction",
    content: [
      { type: "text", content: 'Welcome to the website operated by Vantory Labs LTD ("Company", "we", "our", or "us").' },
      { type: "text", content: "Registered Address:\n109-111 Fulham Palace Road\nLondon\nW6 8JA\nUnited Kingdom" },
      { type: "text", content: "Contact Information:\nEmail: support@vantorylabs.com\nPhone: +447451257361" },
      { type: "text", content: 'These Terms of Use ("Terms") govern your access to and use of this website, including all content, features, functionality, and related services made available through it (collectively, the "Services").' },
      { type: "text", content: "By accessing or using the Site, you acknowledge that you have read, understood, and agreed to be bound by these Terms and our Privacy Policy. If you do not agree with these Terms, you must discontinue use of the Site immediately." },
    ],
  },
  {
    title: "2. Definitions",
    content: [
      { type: "text", content: "For the purposes of these Terms:" },
      {
        type: "bullets",
        items: [
          '"Site" means this website and all related pages, features, and materials',
          '"Company" refers to Vantory Labs LTD',
          '"Services" means any information, tools, functionality, or content provided through the Site',
          '"User", "you", or "your" means any individual or entity accessing or using the Site',
          '"Content" includes text, graphics, images, branding, software, logos, icons, and other materials displayed on the Site',
        ],
      },
    ],
  },
  {
    title: "3. Eligibility",
    content: [
      { type: "text", content: "By using the Site, you represent and warrant that:" },
      {
        type: "bullets",
        items: [
          "You are at least 18 years old or the legal age of majority in your jurisdiction",
          "You have the legal capacity to enter into binding agreements",
          "You will comply with all applicable laws and regulations while using the Site",
        ],
      },
    ],
  },
  {
    title: "4. Permitted Use",
    content: [
      { type: "text", content: "You agree to use the Site only for lawful purposes and in accordance with these Terms." },
      { type: "text", content: "You agree not to:" },
      {
        type: "bullets",
        items: [
          "Use the Site in violation of applicable laws or regulations",
          "Attempt to gain unauthorized access to systems, accounts, or networks",
          "Interfere with the operation, integrity, or security of the Site",
          "Use automated systems, bots, scrapers, or similar technologies without authorization",
          "Engage in conduct that could damage the Company, the Site, or other users",
        ],
      },
      { type: "text", content: "We reserve the right to restrict or terminate access for users who violate these Terms." },
    ],
  },
  {
    title: "5. Intellectual Property Rights",
    content: [
      { type: "text", content: "All Content and materials available on the Site are owned by or licensed to Vantory Labs LTD and are protected by intellectual property and copyright laws." },
      { type: "text", content: "Subject to these Terms, we grant you a limited, revocable, non-exclusive, non-transferable license to access and use the Site for personal or internal business purposes." },
      { type: "text", content: "You may not:" },
      {
        type: "bullets",
        items: [
          "Copy, reproduce, distribute, or publicly display Site Content without prior written permission",
          "Modify, reverse engineer, or create derivative works based on the Site or its Content",
          "Use Company trademarks, branding, or logos without authorization",
        ],
      },
    ],
  },
  {
    title: "6. User Communications and Submitted Content",
    content: [
      { type: "text", content: "If you submit inquiries, feedback, communications, or other materials through the Site:" },
      {
        type: "bullets",
        items: [
          "You retain ownership of your content",
          "You grant the Company a limited, royalty-free license to use such content solely for customer support, operational, or service-related purposes",
          "You confirm that your submissions do not violate applicable laws or third-party rights",
        ],
      },
      { type: "text", content: "We reserve the right to remove or refuse any content that we reasonably believe violates these Terms." },
    ],
  },
  {
    title: "7. Third-Party Services",
    content: [
      { type: "text", content: "The Site may contain links to third-party websites, products, or services." },
      { type: "text", content: "We do not own or control third-party services and are not responsible for:" },
      {
        type: "bullets",
        items: [
          "Their content or policies",
          "Their accuracy or reliability",
          "Their availability or security practices",
        ],
      },
      { type: "text", content: "Your use of third-party services is at your own risk and subject to their own terms and policies." },
    ],
  },
  {
    title: "8. No Medical Advice",
    content: [
      { type: "text", content: "Any health-related information provided through the Site is for informational and general educational purposes only." },
      { type: "text", content: "The Site and Services are not intended to provide medical advice, diagnosis, or treatment and should not be relied upon as a substitute for professional healthcare advice." },
      { type: "text", content: "Always seek guidance from qualified healthcare professionals regarding medical concerns." },
    ],
  },
  {
    title: "9. Disclaimer of Warranties",
    content: [
      { type: "text", content: '"The Site and all Services are provided on an "as is" and "as available" basis.' },
      { type: "text", content: "To the fullest extent permitted by applicable law, Vantory Labs LTD disclaims all warranties, whether express or implied, including warranties of:" },
      {
        type: "bullets",
        items: [
          "Merchantability",
          "Fitness for a particular purpose",
          "Non-infringement",
          "Availability, accuracy, or reliability of the Services",
        ],
      },
      { type: "text", content: "We do not guarantee that the Site will operate uninterrupted, error-free, secure, or free from viruses or harmful components." },
    ],
  },
  {
    title: "10. Limitation of Liability",
    content: [
      { type: "text", content: "To the maximum extent permitted by law, Vantory Labs LTD and its affiliates, directors, employees, or partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including:" },
      {
        type: "bullets",
        items: [
          "Loss of profits",
          "Loss of revenue",
          "Loss of business opportunities",
          "Loss of data",
          "Service interruptions",
        ],
      },
      { type: "text", content: "arising out of or related to your use of, or inability to use, the Site or Services." },
      { type: "text", content: "Nothing in these Terms excludes liability that cannot be limited or excluded under applicable law." },
    ],
  },
  {
    title: "11. Payments and Subscriptions",
    content: [
      { type: "text", content: "Certain services or products may be offered on a paid or subscription basis." },
      { type: "text", content: "Where applicable:" },
      {
        type: "bullets",
        items: [
          "Pricing and billing details will be disclosed before purchase",
          "Subscriptions may renew automatically unless canceled before the renewal date",
          "Users are responsible for applicable taxes and fees",
          "Payments may be processed through third-party payment providers such as Stripe or Apple",
        ],
      },
      { type: "text", content: "Refund eligibility, if applicable, is governed by our Refund Policy." },
    ],
  },
  {
    title: "12. User Responsibility",
    content: [
      { type: "text", content: "You are responsible for:" },
      {
        type: "bullets",
        items: [
          "Ensuring your use of the Site complies with applicable laws",
          "Maintaining the security of your devices and credentials",
          "Any activity conducted through your use of the Site",
        ],
      },
      { type: "text", content: "You acknowledge that use of online services carries inherent risks and that you use the Site at your own discretion." },
    ],
  },
  {
    title: "13. Privacy and Data Protection",
    content: [
      { type: "text", content: "Your use of the Site is also governed by our Privacy Policy." },
      { type: "text", content: "We process personal data in accordance with applicable privacy and data protection laws, including the UK GDPR where applicable." },
    ],
  },
  {
    title: "14. Changes to the Site and Terms",
    content: [
      { type: "text", content: "We reserve the right to:" },
      {
        type: "bullets",
        items: [
          "Modify, suspend, or discontinue any part of the Site at any time",
          "Update or revise these Terms periodically",
        ],
      },
      { type: "text", content: 'Updated versions will be posted on this page with a revised "Last updated" date.' },
      { type: "text", content: "Continued use of the Site after changes become effective constitutes acceptance of the updated Terms." },
    ],
  },
  {
    title: "15. Suspension and Termination",
    content: [
      { type: "text", content: "We may suspend, restrict, or terminate access to the Site without prior notice if:" },
      {
        type: "bullets",
        items: [
          "You violate these Terms",
          "Your activity creates legal, operational, or security risks",
          "Required by law, regulation, or governmental authority",
        ],
      },
    ],
  },
  {
    title: "16. Governing Law",
    content: [
      { type: "text", content: "These Terms shall be governed by and interpreted in accordance with the laws of England and Wales." },
      { type: "text", content: "Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales." },
    ],
  },
  {
    title: "17. Severability",
    content: [
      { type: "text", content: "If any provision of these Terms is found to be unlawful, invalid, or unenforceable, the remaining provisions shall remain in full force and effect." },
    ],
  },
  {
    title: "18. Entire Agreement",
    content: [
      { type: "text", content: "These Terms, together with our Privacy Policy and any additional legal notices published on the Site, constitute the complete agreement between you and Vantory Labs LTD regarding use of the Site and Services." },
    ],
  },
  {
    title: "19. Contact Information",
    content: [
      { type: "text", content: "Vantory Labs LTD\n109-111 Fulham Palace Road\nLondon\nW6 8JA\nUnited Kingdom" },
      { type: "text", content: "Email: support@vantorylabs.com\nPhone: +447451257361" },
    ],
  },
];

export default function TermsPage() {
  const heroRef = useReveal<HTMLDivElement>();

  return (
    <SiteLayout navbarVariant="solid">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[420px] overflow-hidden bg-ink">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink" />
        <div
          ref={heroRef}
          className="relative h-full flex flex-col justify-end mx-auto max-w-[1320px] px-6 md:px-10 pb-16"
        >
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-cream/60 mb-4 block">
              Legal
            </span>
            <h1 className="text-display text-5xl md:text-7xl text-cream leading-[0.95]">
              Terms of <span className="italic font-light">Use</span>.
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-cream py-16 md:py-24">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="max-w-3xl" data-reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-ink/50 hover:text-ink transition-colors mb-10 group"
            >
              <i className="ri-arrow-left-line text-sm group-hover:-translate-x-0.5 transition-transform" />
              Back to Home
            </Link>
            <p className="text-ink/60 text-sm mb-12">
              Last updated: May 21, 2026
            </p>

            <div className="space-y-12">
              {sections.map((s) => (
                <div key={s.title}>
                  <h3 className="text-display text-xl md:text-2xl mb-4">{s.title}</h3>
                  <div className="space-y-3">
                    {s.content.map((item, i) =>
                      item.type === "text" ? (
                        <p
                          key={i}
                          className="text-ink/70 text-sm md:text-base leading-relaxed whitespace-pre-line"
                        >
                          {item.content}
                        </p>
                      ) : (
                        <ul key={i} className="space-y-1.5 pl-1">
                          {item.items.map((bullet, j) => (
                            <li key={j} className="flex items-start gap-2.5 text-ink/70 text-sm md:text-base leading-relaxed">
                              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ink/40 shrink-0" />
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
